import React, { useState, useRef, useEffect } from 'react';
import { COLUMN_DEFINITIONS } from './ProjectsTable';

/**
 * ColumnVisibilityToggle Component
 * Dropdown menu to toggle column visibility
 */
export default function ColumnVisibilityToggle({ visibility, onToggle }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Count visible columns (excluding required ones)
  const visibleCount = COLUMN_DEFINITIONS.filter(
    col => !col.required && visibility[col.key]
  ).length;
  const totalOptional = COLUMN_DEFINITIONS.filter(col => !col.required).length;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {/* Columns icon */}
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
          />
        </svg>
        <span>Columns</span>
        <span className="text-xs text-gray-500">({visibleCount}/{totalOptional})</span>
        {/* Chevron */}
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 uppercase px-2 py-1 mb-1">
              Toggle Columns
            </div>
            {COLUMN_DEFINITIONS.map(col => (
              <label
                key={col.key}
                className={`flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 ${
                  col.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                <input
                  type="checkbox"
                  checked={col.required || visibility[col.key]}
                  disabled={col.required}
                  onChange={() => !col.required && onToggle(col.key)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
                />
                <span className="text-sm text-gray-700">{col.label}</span>
                {col.required && (
                  <span className="text-xs text-gray-400 ml-auto">Required</span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
